/**
 * WR Beautiful — Collection AJAX Filtering
 * Handles filter/sort/pagination via Section Rendering API + pushState.
 * No full page reloads — fetches ?section_id=main-collection for fresh HTML.
 */
(function () {
  'use strict';

  var SECTION_ID = 'main-collection';
  var CONTAINER_SEL = '#wr-collection';
  var GRID_CONTAINER_SEL = '#wr-product-grid-container';
  var TOOLBAR_SEL = '#wr-collection-toolbar';
  var ACTIVE_FILTERS_SEL = '#wr-active-filters';
  var COUNT_SEL = '#wr-product-count';
  var PAGINATION_SEL = '#wr-pagination';
  var LOADING_SEL = '#wr-collection-loading';
  var DRAWER_SEL = '#wr-filter-drawer';

  var isLoading = false;
  var scrollLockY = 0;

  // ---- Helpers ----

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  // ---- AJAX fetch + render ----

  function fetchAndRender(url, pushToHistory) {
    if (isLoading) return;
    isLoading = true;

    var loading = qs(LOADING_SEL);
    if (loading) loading.removeAttribute('hidden');

    // Build the section URL
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    var fetchUrl = url + separator + 'section_id=' + SECTION_ID;

    fetch(fetchUrl)
      .then(function (res) {
        if (!res.ok) throw new Error('Network error: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        renderSection(html);

        if (pushToHistory) {
          history.pushState({ wrCollection: true }, '', url);
        }

        // Scroll to toolbar
        var toolbar = qs(TOOLBAR_SEL);
        if (toolbar) {
          var headerHeight = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '64',
            10
          );
          var top = toolbar.getBoundingClientRect().top + window.pageYOffset - headerHeight - 8;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
      })
      .catch(function (err) {
        console.error('[WR Collection] Fetch error:', err);
        // Fallback: full reload
        window.location.href = url;
      })
      .finally(function () {
        isLoading = false;
        var loading = qs(LOADING_SEL);
        if (loading) loading.setAttribute('hidden', '');
      });
  }

  function renderSection(html) {
    // Parse the HTML fragment
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');

    // Replace the grid container
    var newGrid = qs(GRID_CONTAINER_SEL, doc);
    var oldGrid = qs(GRID_CONTAINER_SEL);
    if (newGrid && oldGrid) {
      oldGrid.innerHTML = newGrid.innerHTML;
    }

    // Replace the toolbar (filter states + sort)
    var newToolbar = qs(TOOLBAR_SEL, doc);
    var oldToolbar = qs(TOOLBAR_SEL);
    if (newToolbar && oldToolbar) {
      oldToolbar.innerHTML = newToolbar.innerHTML;
    }

    // Replace active filters
    var newActiveFilters = qs(ACTIVE_FILTERS_SEL, doc);
    var oldActiveFilters = qs(ACTIVE_FILTERS_SEL);
    if (newActiveFilters && oldActiveFilters) {
      oldActiveFilters.outerHTML = newActiveFilters.outerHTML;
    } else if (newActiveFilters && !oldActiveFilters) {
      // Insert after toolbar
      var toolbar = qs(TOOLBAR_SEL);
      if (toolbar) {
        toolbar.insertAdjacentHTML('afterend', newActiveFilters.outerHTML);
      }
    } else if (!newActiveFilters && oldActiveFilters) {
      oldActiveFilters.remove();
    }

    // Replace count
    var newCount = qs(COUNT_SEL, doc);
    var oldCount = qs(COUNT_SEL);
    if (newCount && oldCount) {
      oldCount.innerHTML = newCount.innerHTML;
    }

    // Replace filter drawer body
    var newDrawer = qs(DRAWER_SEL, doc);
    var oldDrawer = qs(DRAWER_SEL);
    if (newDrawer && oldDrawer) {
      oldDrawer.innerHTML = newDrawer.innerHTML;
    }

    // All events use delegation on the container — no re-binding needed.
  }

  // ---- URL building ----

  function buildUrlFromCheckbox(checkbox) {
    // Each checkbox has data-filter-url which is the URL Shopify provides
    // for toggling that filter value
    return checkbox.getAttribute('data-filter-url');
  }

  function buildSortUrl(sortValue) {
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortValue);
    // Reset to page 1 when sorting
    url.searchParams.delete('page');
    return url.pathname + url.search;
  }

  function buildPriceUrl() {
    var url = new URL(window.location.href);

    // Get price inputs from both desktop and mobile
    var minInputs = qsa('[data-price-min]');
    var maxInputs = qsa('[data-price-max]');

    var minVal = '';
    var maxVal = '';

    // Use the first non-empty value found
    for (var i = 0; i < minInputs.length; i++) {
      if (minInputs[i].value) {
        minVal = minInputs[i].value;
        break;
      }
    }
    for (var j = 0; j < maxInputs.length; j++) {
      if (maxInputs[j].value) {
        maxVal = maxInputs[j].value;
        break;
      }
    }

    // Determine the param names from the inputs
    var minInput = minInputs[0];
    var maxInput = maxInputs[0];

    if (minInput && maxInput) {
      var minName = minInput.getAttribute('name');
      var maxName = maxInput.getAttribute('name');

      if (minVal) {
        // Shopify expects price in cents (kuruş) for some setups, but
        // the Storefront Filtering API expects the raw number in the currency unit
        url.searchParams.set(minName, minVal);
      } else {
        url.searchParams.delete(minName);
      }

      if (maxVal) {
        url.searchParams.set(maxName, maxVal);
      } else {
        url.searchParams.delete(maxName);
      }
    }

    // Reset page
    url.searchParams.delete('page');
    return url.pathname + url.search;
  }

  // ---- Filter dropdown open/close ----

  function toggleDropdown(trigger) {
    var expanded = trigger.getAttribute('aria-expanded') === 'true';
    var panelId = trigger.getAttribute('aria-controls');
    var panel = document.getElementById(panelId);

    // Close all other dropdowns first
    qsa('[data-filter-trigger]').forEach(function (t) {
      if (t !== trigger) {
        t.setAttribute('aria-expanded', 'false');
        var otherId = t.getAttribute('aria-controls');
        var otherPanel = document.getElementById(otherId);
        if (otherPanel) otherPanel.setAttribute('hidden', '');
      }
    });

    if (expanded) {
      trigger.setAttribute('aria-expanded', 'false');
      if (panel) panel.setAttribute('hidden', '');
    } else {
      trigger.setAttribute('aria-expanded', 'true');
      if (panel) panel.removeAttribute('hidden');
    }
  }

  function closeAllDropdowns() {
    qsa('[data-filter-trigger]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
      var panelId = t.getAttribute('aria-controls');
      var panel = document.getElementById(panelId);
      if (panel) panel.setAttribute('hidden', '');
    });
  }

  // ---- Mobile drawer ----

  function openMobileDrawer() {
    var drawer = qs(DRAWER_SEL);
    if (!drawer) return;

    scrollLockY = window.pageYOffset;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollLockY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';

    drawer.setAttribute('aria-hidden', 'false');

    // Focus the close button
    var closeBtn = qs('[data-mobile-filter-close]', drawer);
    if (closeBtn) {
      setTimeout(function () { closeBtn.focus(); }, 100);
    }
  }

  function closeMobileDrawer() {
    var drawer = qs(DRAWER_SEL);
    if (!drawer) return;

    drawer.setAttribute('aria-hidden', 'true');

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    window.scrollTo(0, scrollLockY);
  }

  // Main delegated event handler on the collection container
  function handleClick(e) {
    var target = e.target;

    // Filter dropdown triggers
    var trigger = target.closest('[data-filter-trigger]');
    if (trigger) {
      e.preventDefault();
      toggleDropdown(trigger);
      return;
    }

    // Filter checkbox
    var checkbox = target.closest('[data-filter-checkbox]');
    if (checkbox && checkbox.tagName === 'INPUT') {
      var url = buildUrlFromCheckbox(checkbox);
      if (url) {
        e.preventDefault();
        fetchAndRender(url, true);
      }
      return;
    }

    // Price apply button
    var priceApply = target.closest('[data-price-apply]');
    if (priceApply) {
      e.preventDefault();
      var priceUrl = buildPriceUrl();
      fetchAndRender(priceUrl, true);
      return;
    }

    // Active filter pill remove
    var filterRemove = target.closest('[data-filter-remove]');
    if (filterRemove) {
      e.preventDefault();
      var removeUrl = filterRemove.getAttribute('href');
      if (removeUrl) {
        fetchAndRender(removeUrl, true);
      }
      return;
    }

    // Clear all filters
    var clearAll = target.closest('[data-clear-all]');
    if (clearAll) {
      e.preventDefault();
      var clearUrl = clearAll.getAttribute('href');
      if (clearUrl) {
        fetchAndRender(clearUrl, true);
      }
      return;
    }

    // Pagination
    var pageLink = target.closest('[data-pagination-link]');
    if (pageLink) {
      e.preventDefault();
      var pageUrl = pageLink.getAttribute('href');
      if (pageUrl) {
        fetchAndRender(pageUrl, true);
      }
      return;
    }

    // Mobile filter open
    var mobileOpen = target.closest('[data-mobile-filter-open]');
    if (mobileOpen) {
      e.preventDefault();
      openMobileDrawer();
      return;
    }

    // Mobile filter close
    var mobileClose = target.closest('[data-mobile-filter-close]');
    if (mobileClose) {
      e.preventDefault();
      closeMobileDrawer();
      return;
    }
  }

  function handleChange(e) {
    var target = e.target;

    // Sort select
    if (target.matches('[data-sort-select]')) {
      var sortUrl = buildSortUrl(target.value);
      fetchAndRender(sortUrl, true);
      return;
    }

    // Filter checkbox (change event)
    if (target.matches('[data-filter-checkbox]')) {
      var url = buildUrlFromCheckbox(target);
      if (url) {
        fetchAndRender(url, true);
      }
    }
  }

  // ---- Close dropdowns on outside click ----

  function handleDocumentClick(e) {
    if (!e.target.closest('[data-filter-dropdown]')) {
      closeAllDropdowns();
    }
  }

  // ---- Keyboard handling ----

  function handleKeydown(e) {
    // Escape closes dropdowns and mobile drawer
    if (e.key === 'Escape') {
      closeAllDropdowns();

      var drawer = qs(DRAWER_SEL);
      if (drawer && drawer.getAttribute('aria-hidden') === 'false') {
        closeMobileDrawer();
        var openBtn = qs('[data-mobile-filter-open]');
        if (openBtn) openBtn.focus();
      }
    }
  }

  // ---- Popstate (back/forward) ----

  function handlePopstate() {
    fetchAndRender(window.location.href, false);
  }

  // ---- Init ----

  function init() {
    var container = qs(CONTAINER_SEL);
    if (!container) return;

    // Delegated click handler
    container.addEventListener('click', handleClick);

    // Delegated change handler (for sort selects and filter checkboxes)
    container.addEventListener('change', handleChange);

    // Close dropdowns on outside click
    document.addEventListener('click', handleDocumentClick);

    // Keyboard
    document.addEventListener('keydown', handleKeydown);

    // Popstate for back/forward
    window.addEventListener('popstate', handlePopstate);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
